import { Alert, AlertDescription, AlertTitle } from "@babackapp/ui/components/alert";
import { AlertCircle, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";


type TemplateValidationItem = {
  type: "missing_translation" | "missing_subject_translation";
  severity: "error" | "warning";
  blockId?: string;
};


interface ValidationErrorsDisplayProps {
  validationItems: TemplateValidationItem[];
}


export function ValidationErrorsDisplay({ validationItems }: ValidationErrorsDisplayProps) {
  const { t } = useTranslation("trackoffice");

  // Group validation items by type and severity
  const subjectErrors = validationItems.filter(
    (item) => item.type === "missing_subject_translation" && item.severity === "error",
  );
  const subjectWarnings = validationItems.filter(
    (item) => item.type === "missing_subject_translation" && item.severity === "warning",
  );
  const blockErrors = validationItems.filter(
    (item) => item.type === "missing_translation" && item.severity === "error",
  );
  const blockWarnings = validationItems.filter(
    (item) => item.type === "missing_translation" && item.severity === "warning",
  );

  return (
    <div className="space-y-4">
      {subjectErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            {t(
              "notificationsTemplatesEditor.validation.subjectErrors.title",
              "Subject Translation Errors",
            )}
          </AlertTitle>
          <AlertDescription>
            {t(
              "notificationsTemplatesEditor.validation.subjectErrors.description",
              {
                count: subjectErrors.length,
                defaultValue: `${subjectErrors.length} missing translation${subjectErrors.length > 1 ? "s" : ""}`,
              },
            )}
          </AlertDescription>
        </Alert>
      )}

      {blockErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            {t(
              "notificationsTemplatesEditor.validation.blockErrors.title",
              "Block Translation Errors",
            )}
          </AlertTitle>
          <AlertDescription>
            {t(
              "notificationsTemplatesEditor.validation.blockErrors.description",
              {
                count: blockErrors.length,
                defaultValue: `${blockErrors.length} block${blockErrors.length > 1 ? "s" : ""} missing translation in default language`,
              },
            )}
          </AlertDescription>
        </Alert>
      )}

      {subjectWarnings.length > 0 && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {t(
              "notificationsTemplatesEditor.validation.subjectWarnings.title",
              "Subject Translation Warnings",
            )}
          </AlertTitle>
          <AlertDescription>
            {t(
              "notificationsTemplatesEditor.validation.subjectWarnings.description",
              {
                count: subjectWarnings.length,
                defaultValue: `${subjectWarnings.length} missing translation${subjectWarnings.length > 1 ? "s" : ""}`,
              },
            )}
          </AlertDescription>
        </Alert>
      )}

      {blockWarnings.length > 0 && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {t(
              "notificationsTemplatesEditor.validation.blockWarnings.title",
              "Block Translation Warnings",
            )}
          </AlertTitle>
          <AlertDescription>
            {t(
              "notificationsTemplatesEditor.validation.blockWarnings.description",
              {
                count: blockWarnings.length,
                defaultValue: `${blockWarnings.length} block${blockWarnings.length > 1 ? "s" : ""} missing translation`,
              },
            )}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
